/**
 * The 3PL's Replace review (Phase 92, UI-SPEC 3.4; D-11, D-12, D-23), shared
 * by the inline panel in `EditSkuModal` (92-31) and `ReplaceBlankDialog`
 * (92-31b), plus the replacement picker for one blank (UI-SPEC 3.2).
 *
 * The review renders ONE server dry run (`previewReplaceBlank`, through
 * `usePreviewReplaceBlank`): the headline "{products} products for {clients}
 * clients", the sub-line about new orders, and one row per client variant on
 * the blank with the print SKU now and from the next order. Rows the server
 * refused stay on the current blank and say why in amber; an `in_use` row
 * names only the 3PL's own location, never the org holding the print SKU
 * (D-12). Each row shows only its own client's product, and only the 3PL sees
 * this screen (its own clients, T-92-31-03).
 *
 * States: loading = three `h-10` skeleton rows; a failed read =
 * `SectionError compact` with "Check changes again"; the cap refusal = its own
 * line. The confirm lives with the caller and is live only when
 * `reviewAllowsReplace` says so. More than 12 rows sit in a `max-h-80
 * overflow-y-auto` box (never a sideways scroll, DESIGN.md §22).
 */
import { useEffect, useMemo, useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronsUpDown, Link2, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import type { OwnerSharedCatalogItem } from '@/features/product-sync/lib/shared-stock';
import type { KeptRead, ReplacePreview, ReplacePreviewRow } from '../../hooks/useSharedSkus';

/** UI 22: past this many rows the table scrolls inside its own box. */
const SCROLL_AFTER_ROWS = 12;
const CATALOG_PAGE_LIMIT = 50;

export interface ReplaceReviewProps {
  /** The review hook's state (`usePreviewReplaceBlank`). */
  review: Pick<KeptRead<ReplacePreview>, 'data' | 'loading' | 'error'>;
  /** "Check changes again": re-sends the same dry run. */
  onRetry: () => void;
  /**
   * Default true. False when the caller already shows the "orders already
   * placed" sentence (the bulk dialog's description, 92-31b), so it is not
   * read twice.
   */
  showSubline?: boolean;
  /** The no-rows line; the bulk dialog says "these blanks". */
  noRowsText?: string;
  className?: string;
}

export function ReplaceReview({ review, onRetry, showSubline = true, noRowsText, className }: ReplaceReviewProps) {
  const { t } = useTranslation('inventory');
  const { data, loading, error } = review;

  let body: ReactNode;
  if (error || (data && !data.ok && data.refusal !== 'too_many_rows')) {
    body = (
      <SectionError
        variant="compact"
        body={t('sharedSkus.replace.reviewError')}
        onRetry={onRetry}
        retryLabel={t('sharedSkus.replace.reviewRetry')}
        retrying={loading}
      />
    );
  } else if (!data) {
    body = (
      <div className="space-y-2" aria-hidden="true" data-testid="replace-review-skeleton">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (!data.ok) {
    body = (
      <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
        {t('sharedSkus.replace.tooManyRows')}
      </NoteLine>
    );
  } else if (data.rows.length === 0) {
    body = <NoteLine>{noRowsText ?? t('sharedSkus.replace.noRows')}</NoteLine>;
  } else {
    body = <ReviewTable data={data} showSubline={showSubline} />;
  }

  return (
    <section
      className={cn('space-y-2', className)}
      aria-busy={loading}
      aria-label={t('sharedSkus.replace.reviewLabel')}
    >
      {/* Always mounted, so the loading line is announced once. */}
      <p role="status" className="sr-only">
        {loading ? t('sharedSkus.replace.checking') : ''}
      </p>
      {body}
    </section>
  );
}

function ReviewTable({ data, showSubline }: { data: Extract<ReplacePreview, { ok: true }>; showSubline: boolean }) {
  const { t } = useTranslation('inventory');
  const scrolls = data.rows.length > SCROLL_AFTER_ROWS;
  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">
          {t('sharedSkus.replace.headline', {
            products: t('sharedSkus.replace.headlineProducts', { count: data.products }),
            clients: t('sharedSkus.replace.headlineClients', { count: data.clients }),
          })}
        </p>
        {showSubline && (
          <p className="text-xs leading-normal text-muted-foreground">{t('sharedSkus.replace.subline')}</p>
        )}
        {/* D-28: a Replace never writes to a client store. */}
        {data.changes > 0 && (
          <p className="text-xs leading-normal text-muted-foreground">{t('sharedSkus.replace.storesCatchUp')}</p>
        )}
      </div>
      {data.changes > 0 ? (
        <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal">
          {t('sharedSkus.replace.fileWarning')}
        </NoteLine>
      ) : (
        <NoteLine className="leading-normal">{t('sharedSkus.replace.nothingChanges')}</NoteLine>
      )}
      <div
        className={cn('rounded-md border', scrolls && 'max-h-80 overflow-y-auto')}
        data-testid="replace-review-rows"
        data-scrolls={scrolls ? 'true' : 'false'}
      >
        <table className="w-full table-fixed border-collapse text-xs">
          <thead className={cn('bg-background', scrolls && 'sticky top-0 z-10')}>
            <tr className="border-b text-left text-muted-foreground">
              <th scope="col" className="w-[22%] px-2 py-2 font-medium">
                {t('sharedSkus.replace.colClient')}
              </th>
              <th scope="col" className="w-[26%] px-2 py-2 font-medium">
                {t('sharedSkus.replace.colProduct')}
              </th>
              <th scope="col" className="w-[26%] px-2 py-2 font-medium">
                {t('sharedSkus.replace.colNow')}
              </th>
              <th scope="col" className="w-[26%] px-2 py-2 font-medium">
                {t('sharedSkus.replace.colNext')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map((row) => (
              <ReviewRow key={`${row.clientOrganizationId}:${row.shopifyVariantId}:${row.fromSkuId}`} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewRow({ row }: { row: ReplacePreviewRow }) {
  const { t } = useTranslation('inventory');
  return (
    <tr className="align-top">
      <td className="min-w-0 px-2 py-2">
        <ClientOrgBadge orgName={row.clientName} srLabel={row.clientName} className="min-w-0 break-words" />
      </td>
      <td className="min-w-0 break-words px-2 py-2 leading-normal">
        {row.productTitle ?? <span className="text-muted-foreground">{t('sharedSkus.replace.untitled')}</span>}
      </td>
      <td className="min-w-0 px-2 py-2">
        {row.printSkuNow ? (
          <span className="block min-w-0 break-all font-mono">{row.printSkuNow}</span>
        ) : (
          <span className="text-muted-foreground">{t('sharedSkus.replace.notSynced')}</span>
        )}
      </td>
      <td className="min-w-0 px-2 py-2">
        <NextCell row={row} />
      </td>
    </tr>
  );
}

function NextCell({ row }: { row: ReplacePreviewRow }) {
  const { t } = useTranslation('inventory');
  if (row.outcome === 'replace') {
    return row.printSkuNext && row.printSkuNext !== row.printSkuNow ? (
      <span className="block min-w-0 break-all font-mono font-medium">{row.printSkuNext}</span>
    ) : row.printSkuNext ? (
      <span className="text-muted-foreground">{t('sharedSkus.replace.noChange')}</span>
    ) : (
      <span className="text-muted-foreground">{t('sharedSkus.replace.notSynced')}</span>
    );
  }
  if (row.outcome === 'no_change') {
    return <span className="text-muted-foreground">{t('sharedSkus.replace.noChange')}</span>;
  }
  const reason =
    row.outcome === 'in_use'
      ? row.locationName
        ? t('sharedSkus.replace.staysInUse', { location: row.locationName })
        : t('sharedSkus.replace.staysInUseNoLocation')
      : row.outcome === 'code_clash'
        ? t('sharedSkus.replace.staysCodeClash')
        : t('sharedSkus.replace.staysConsentOff');
  return (
    <span className="flex items-start gap-1 leading-normal text-amber-800">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-700" aria-hidden="true" />
      <span className="min-w-0 [overflow-wrap:anywhere]">{reason}</span>
    </span>
  );
}

export interface ReplacementBlankPickerProps {
  /** Trigger id, for the label. */
  id: string;
  /** The caller's own org: the picker lists its own shared blanks. */
  ownerOrganizationId: string;
  /** The blank being replaced, never offered. */
  currentSkuId: string;
  /** Its product type: blanks of the same type list first. */
  productType: string | null;
  value: OwnerSharedCatalogItem | null;
  onChange: (item: OwnerSharedCatalogItem) => void;
  /** Focus lands here when the panel opens. */
  triggerRef?: Ref<HTMLButtonElement>;
}

/**
 * "Replacement blank" (UI-SPEC 3.2): the caller's other shared blanks from
 * `inventory.listOwnerSharedCatalog` (the caller as its own owner, as today's
 * back-up picker reads it), searched on the server, same product type first.
 * Never free text. With no other shared blank it says so and offers nothing
 * to confirm (UI 5).
 */
export function ReplacementBlankPicker({
  id,
  ownerOrganizationId,
  currentSkuId,
  productType,
  value,
  onChange,
  triggerRef,
}: ReplacementBlankPickerProps) {
  const { t } = useTranslation('inventory');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const catalog = trpc.inventory.listOwnerSharedCatalog.useQuery(
    { ownerOrganizationId, search: debouncedSearch || undefined, limit: CATALOG_PAGE_LIMIT },
    { enabled: Boolean(ownerOrganizationId), placeholderData: keepPreviousData, staleTime: 30_000, retry: 1 },
  );

  const { sameType, others } = useMemo(() => {
    const items = (catalog.data?.items ?? []).filter((item) => item.id !== currentSkuId);
    const type = productType?.trim().toLowerCase() ?? '';
    const same = type ? items.filter((item) => (item.category ?? '').trim().toLowerCase() === type) : [];
    const sameIds = new Set(same.map((item) => item.id));
    return { sameType: same, others: items.filter((item) => !sameIds.has(item.id)) };
  }, [catalog.data, currentSkuId, productType]);
  const total = sameType.length + others.length;
  const helperId = `${id}-helper`;

  const label = (
    <Label htmlFor={id} className="text-sm">
      {t('sharedSkus.replace.pickerLabel')}
      <span aria-hidden="true" className="ml-0.5 text-destructive">
        *
      </span>
    </Label>
  );

  // UI 5: the owner shares no other blank (not a search miss).
  if (!debouncedSearch && catalog.isSuccess && !catalog.isPlaceholderData && total === 0) {
    return (
      <div className="grid gap-1.5">
        {label}
        <NoteLine tone="warning" icon={AlertTriangle} className="leading-normal" id={helperId}>
          {t('sharedSkus.replace.noOtherBlanks')}
        </NoteLine>
      </div>
    );
  }

  if (!debouncedSearch && catalog.isError) {
    return (
      <div className="grid gap-1.5">
        {label}
        <SectionError
          variant="inline"
          body={t('sharedSkus.replace.blanksError')}
          onRetry={() => void catalog.refetch()}
          retryLabel={t('sharedSkus.replace.blanksRetry')}
          retrying={catalog.isFetching}
        />
      </div>
    );
  }

  const missing = !value;
  const renderItem = (item: OwnerSharedCatalogItem) => (
    <CommandItem
      key={item.id}
      value={item.id}
      onSelect={() => {
        onChange(item);
        setOpen(false);
      }}
      className="gap-2"
    >
      <Check
        className={cn('h-3.5 w-3.5 shrink-0 text-threadheads-purple', value?.id === item.id ? 'opacity-100' : 'opacity-0')}
        aria-hidden="true"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-mono text-xs font-medium">{item.sku}</span>
        <span className="truncate text-xs text-muted-foreground">
          {[item.title, item.size, item.color].filter(Boolean).join(' · ') || item.sku}
        </span>
      </span>
    </CommandItem>
  );

  return (
    <div className="grid gap-1.5">
      {label}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-required
            aria-invalid={missing}
            aria-describedby={missing ? helperId : undefined}
            className={cn(
              'h-8 w-full max-w-sm justify-between gap-1.5 px-3 text-xs font-normal transition-colors duration-200 ease-out motion-reduce:transition-none',
              missing && 'border-amber-300 ring-1 ring-amber-100/50',
            )}
          >
            {value ? (
              <span className="flex min-w-0 items-center gap-2">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                <span className="truncate font-mono font-medium">{value.sku}</span>
                {value.title && <span className="truncate text-muted-foreground">{value.title}</span>}
              </span>
            ) : (
              <span className="truncate text-muted-foreground">{t('sharedSkus.replace.pickerPlaceholder')}</span>
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[22rem] max-w-[calc(100vw-2rem)] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={t('sharedSkus.replace.searchPlaceholder')}
            />
            <CommandList>
              {catalog.isError ? (
                <p role="alert" className="px-3 py-6 text-center text-xs text-destructive">
                  {t('sharedSkus.replace.blanksError')}
                </p>
              ) : catalog.isLoading ? (
                <p className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {t('sharedSkus.replace.loadingBlanks')}
                </p>
              ) : (
                <>
                  <CommandEmpty className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {t('sharedSkus.replace.noSearchMatch', { query: debouncedSearch })}
                  </CommandEmpty>
                  {sameType.length > 0 && (
                    <CommandGroup heading={t('sharedSkus.replace.groupSameType')}>{sameType.map(renderItem)}</CommandGroup>
                  )}
                  {others.length > 0 && (
                    <CommandGroup heading={t('sharedSkus.replace.groupOther')}>{others.map(renderItem)}</CommandGroup>
                  )}
                  {catalog.data?.hasMore && (
                    <p className="border-t px-3 py-2 text-xs text-muted-foreground">{t('sharedSkus.replace.moreBlanks')}</p>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {missing && (
        <p id={helperId} className="text-xs text-amber-700">
          {t('sharedSkus.replace.chooseHelper')}
        </p>
      )}
    </div>
  );
}
